import { Route, Routes, useNavigate } from "react-router";
import { Suspense, lazy, useEffect } from "react";
import { postRenderSetup } from "@mobile/init";
import { AuthLayout } from "@mobile/layouts/auth-layout";
import { AppLayout } from "@mobile/layouts/app-layout";
import { ProtectedRoute } from "@mobile/routes/protected-route";
import { WorkspaceRoute } from "@mobile/routes/workspace-route";
import { Loading } from "@repo/share-log-ui/ui/loading";

const LoginPage = lazy(() => import("@mobile/pages/login").then(m => ({ default: m.LoginPage })));
const WelcomePage = lazy(() => import("@mobile/pages/welcome").then(m => ({ default: m.WelcomePage })));
const WorkspaceEmptyPage = lazy(() => import("@mobile/pages/workspace-empty").then(m => ({ default: m.WorkspaceEmptyPage })));
const WorkspaceNewPage = lazy(() => import("@mobile/pages/workspace-new").then(m => ({ default: m.WorkspaceNewPage })));
const WorkspaceDashboardPage = lazy(() => import("@mobile/pages/workspace-dashboard").then(m => ({ default: m.WorkspaceDashboardPage })));
const WorkspaceSettingsPage = lazy(() => import("@mobile/pages/workspace-settings").then(m => ({ default: m.WorkspaceSettingsPage })));
const AdjustmentListPage = lazy(() => import("@mobile/pages/adjustment-list").then(m => ({ default: m.AdjustmentListPage })));
const SpendingGoalsPage = lazy(() => import("@mobile/pages/spending-goals").then(m => ({ default: m.SpendingGoalsPage })));
const AssetPage = lazy(() => import("@mobile/pages/asset").then(m => ({ default: m.AssetPage })));
const AdjustmentNewPage = lazy(() => import("@mobile/pages/adjustment-new").then(m => ({ default: m.AdjustmentNewPage })));
const AdjustmentDetailPage = lazy(() => import("@mobile/pages/adjustment-detail").then(m => ({ default: m.AdjustmentDetailPage })));
const NotificationsPage = lazy(() => import("@mobile/pages/notifications").then(m => ({ default: m.NotificationsPage })));
const MorePage = lazy(() => import("@mobile/pages/more").then(m => ({ default: m.MorePage })));
const ProfilePage = lazy(() => import("@mobile/pages/profile").then(m => ({ default: m.ProfilePage })));
const NotFoundPage = lazy(() => import("@mobile/pages/not-found").then(m => ({ default: m.NotFoundPage })));

export function AppRoutes() {
  const navigate = useNavigate();

  useEffect(() => {
    const cleanup = postRenderSetup({ navigate });
    return cleanup;
  }, []);

  return (
    <Suspense fallback={<Loading className="fixed inset-0" />}>
      <Routes>
        {/* Auth Layout (로그인 전) */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>

        {/* Auth Layout + Protected (프로필 설정) */}
        <Route element={<AuthLayout />}>
          <Route element={<ProtectedRoute />}>
            <Route path="/welcome" element={<WelcomePage />} />
          </Route>
        </Route>

        {/* App Layout + Protected */}
        <Route element={<AppLayout />}>
          <Route element={<ProtectedRoute />}>
            <Route path="/workspace/empty" element={<WorkspaceEmptyPage />} />
            <Route path="/workspace/new" element={<WorkspaceNewPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/more" element={<MorePage />} />
            <Route path="/profile" element={<ProfilePage />} />
          </Route>
        </Route>

        {/* App Layout + Workspace */}
        <Route element={<AppLayout />}>
          <Route element={<ProtectedRoute />}>
            <Route element={<WorkspaceRoute />}>
              <Route path="/" element={<WorkspaceDashboardPage />} />
              <Route
                path="/workspace/:workspaceId"
                element={<WorkspaceDashboardPage />}
              />
              <Route
                path="/workspace/:workspaceId/settings"
                element={<WorkspaceSettingsPage />}
              />
              <Route
                path="/workspace/:workspaceId/spending-goals"
                element={<SpendingGoalsPage />}
              />
              <Route
                path="/workspace/:workspaceId/asset"
                element={<AssetPage />}
              />
              <Route
                path="/workspace/:workspaceId/adjustment"
                element={<AdjustmentListPage />}
              />
              <Route
                path="/workspace/:workspaceId/adjustment/new"
                element={<AdjustmentNewPage />}
              />
              <Route
                path="/workspace/:workspaceId/adjustment/:id"
                element={<AdjustmentDetailPage />}
              />
            </Route>
          </Route>
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

